"use client";

import { useEffect, useRef } from "react";
import { useSession } from "@/lib/auth/auth-client";
import { useCartStore } from "@/lib/store/cartStore";
import { toast } from "@/lib/toast";

/**
 * CartMergeHandler
 *
 * Handles automatic merging of guest cart with authenticated user cart on sign-in.
 * Mounted once at the root layout level.
 *
 * Fixed: `previousAuthState.current` was initialized to `false`, which caused
 * the handler to treat the very first auth resolution (any value) as "just signed in"
 * when the initial state was undefined/pending. Now uses `null` as the initial
 * sentinel so the first real auth resolution never triggers a merge.
 */
export function CartMergeHandler() {
  const { data: session, isPending } = useSession();
  const { sessionId, mergeGuestCart } = useCartStore();
  const hasAttemptedMerge = useRef(false);
  // null = "we haven't seen a resolved auth state yet" (different from false = "not authenticated")
  const previousAuthState = useRef<boolean | null>(null);

  useEffect(() => {
    // Skip while auth is still loading
    if (isPending) return;

    const isAuthenticated = !!session?.user;

    // First time we see a resolved auth state — just record it, never merge
    if (previousAuthState.current === null) {
      previousAuthState.current = isAuthenticated;
      return;
    }

    const wasAuthenticated = previousAuthState.current;
    const justSignedIn = isAuthenticated && !wasAuthenticated;

    // Update for next run
    previousAuthState.current = isAuthenticated;

    // Reset merge flag on sign-out so next sign-in can merge again
    if (!isAuthenticated && wasAuthenticated) {
      hasAttemptedMerge.current = false;
      return;
    }

    // Only merge once when user genuinely signs in AND has a guest sessionId
    if (justSignedIn && sessionId && !hasAttemptedMerge.current) {
      hasAttemptedMerge.current = true;

      mergeGuestCart()
        .then(() => {
          toast.success("Cart merged successfully", {
            description: "Your guest cart items have been added to your account",
            duration: 3000,
          });
        })
        .catch((error) => {
          console.error("[CartMergeHandler] Failed to merge guest cart:", error);
          // Non-fatal: items still accessible via sessionId until it expires
        });
    }
  }, [session, isPending, sessionId, mergeGuestCart]);

  return null;
}
